// VPC IPAM — the AWS lifecycle-state vocabulary, as {intent, label, hint}.
//
// ── WHY THIS EXISTS RATHER THAN A formatStatusLabel() CALL ───────────────────
//
// `formatStatusLabel` (src/utils/statusLabel.js) splits on /[_\-\s]+/ and
// Title-Cases, so IPAM's hyphenated AWS states come out READABLE — "create-in-
// progress" renders "Create In Progress". Readable is not the same as
// meaningful: the generic formatter carries no intent, so every one of the 18
// lifecycle values draws in the same colour and an operator has to already know
// the vocabulary to tell "still working" from "this will never finish".
//
// THE THREE THAT MAKE THIS NON-NEGOTIABLE ARE `isolate-in-progress`,
// `isolate-complete` AND `restore-in-progress`. They do not mean what their
// verbs suggest to anyone who has not read the SDK: they mean THE AWS ACCOUNT
// THAT OWNS THIS RESOURCE LEFT (or is rejoining) THE ORGANIZATION. A vocabulary
// that models only create/modify/delete renders "Isolate Complete" as ordinary
// progress — grey, unremarkable, indistinguishable from a step that is about to
// finish — when the truth is that nothing here will ever finish, no retry will
// help, and the resource may still be metering inside an account Prism can no
// longer reach. That is the opposite of what has happened.
//
// The classification mirrors the backend's ipamLifecycleByState
// (services/ipam_service.go) exactly, including its two non-obvious rows:
//   * delete-in-progress is PENDING, never Gone — DeleteIpam is the call that
//     stops the meter and it can still fail;
//   * restore-in-progress is PENDING, not Isolated — it is the account REJOINING
//     the organization, i.e. the recovery, and it is the one isolate-family value
//     worth waiting on.
//
// ── UNRECOGNISED VALUES DEGRADE, THEY DO NOT BLANK — AND THEY DO NOT PASS ───
//
// `ipamStateMeta` falls back to formatStatusLabel for anything not in the table,
// so the day AWS adds a nineteenth state the chip still reads "Something New"
// rather than rendering empty or crashing.
//
// THE FALLBACK INTENT IS `warning`, AND THAT IS THE WHOLE POINT OF IT.
//
// It is deliberately not `error`: absorbing an unknown value into a decided
// verdict is how a value AWS shipped last week looks like a failure forever.
// But it is emphatically not `neutral` either, which is where this started and
// which was wrong for the subtler reason — `neutral` is the intent worn by
// `delete-complete`, `deprovisioned` and `disassociate-complete`, all of which
// are DESIGNED, benign, decided outcomes. Drawing an unrecognised token in the
// same grey makes an anomaly indistinguishable from a state Prism understands
// and considers fine, which is laundering, and laundering an anomaly into a
// designed state is the exact failure mode this capability keeps producing.
//
// Amber plus the raw value plus "Prism has no rule for this" is the honest
// answer, and it matches discoveryChipIntent's existing precedent in
// IpamDiscoveryColdStart.js: anything unrecognised is drawn as a warning rather
// than folded into a bucket, because guessing is how a broken thing ends up
// looking healthy.
//
// ── TOMBSTONES ──────────────────────────────────────────────────────────────
//
// `describe-ipam-pools` keeps returning `delete-complete` rows for a while after
// a delete [SPIKE-VERIFIED]. JUDGE BY STATE, NEVER BY COUNTING ROWS — a "3
// pools" number derived from `rows.length` counts the dead ones. The backend's
// tree builder already drops gone rows before they reach the wire; this entry
// exists because the per-pool sub-lists do not, and because a tombstone that
// does surface must read as "Deleted", not as a live pool.
import { Chip, Tooltip } from '@mui/material';
import { CK, FONT_BODY, statusChipSx } from '../../portalStyles';
import { formatStatusLabel } from '../../../../utils/statusLabel';

// The freshness caveat. Every one of these values is as of the last refresh
// Prism ran against AWS, not a live poll — a pool created a moment ago
// legitimately still reads "Creating" long after AWS finished.
export const IPAM_STATE_FRESHNESS =
  'This is as of the last refresh Prism ran against AWS, not a live poll.';

// The one sentence Prism says about a departed account, matching the backend's
// ipamIsolatedRefusal so the UI and the API cannot drift apart.
export const IPAM_ISOLATED_SENTENCE =
  'The AWS account that owns this resource has left the organization.';

// Keys are LOWER-CASE. `ipamStateMeta` lower-cases and trims before looking up,
// so RAM's upper-case statuses and EC2's hyphenated ones share one table without
// either having to be spelled twice.
export const IPAM_STATE_META = {
  // --- The 18 IPAM lifecycle values (IpamState, IpamPoolState, IpamScopeState,
  //     IpamResourceDiscoveryState, IpamResourceDiscoveryAssociationState) ---

  'create-in-progress': {
    intent: 'info',
    label: 'Creating',
    hint: 'AWS is still creating this. It settles on its own, usually within a minute — there is nothing to retry.',
  },
  'modify-in-progress': {
    intent: 'info',
    label: 'Updating',
    hint: 'AWS is still applying a change. It settles on its own; a tier change alone spends about 20 seconds here.',
  },
  'delete-in-progress': {
    intent: 'info',
    label: 'Deleting',
    hint: 'AWS is still deleting this. IT IS NOT GONE YET and anything billable about it is still billing until AWS reports the delete complete.',
  },
  'associate-in-progress': {
    intent: 'info',
    label: 'Associating',
    hint: 'AWS is still associating this resource discovery. It settles on its own.',
  },
  'disassociate-in-progress': {
    intent: 'info',
    label: 'Disassociating',
    hint: 'AWS is still disassociating this resource discovery. It settles on its own.',
  },

  'create-complete': {
    intent: 'success',
    label: 'Created',
    hint: 'AWS reports this exists and is usable.',
  },
  'modify-complete': {
    intent: 'success',
    label: 'Updated',
    hint: 'AWS reports the change was applied and this is usable.',
  },
  'associate-complete': {
    intent: 'success',
    label: 'Associated',
    hint: 'AWS reports this resource discovery is associated and reporting.',
  },

  'create-failed': {
    intent: 'error',
    label: 'Create Failed',
    hint: 'AWS could not create this. Nothing was created, so nothing is billing for it — but nothing works either.',
  },
  'modify-failed': {
    intent: 'error',
    label: 'Update Failed',
    hint: 'AWS could not apply the change. The resource still exists at its previous settings.',
  },
  'delete-failed': {
    intent: 'error',
    label: 'Delete Failed',
    hint: 'AWS could not delete this. THE RESOURCE STILL EXISTS and anything billable about it is still billing. This is not "already gone".',
  },
  'associate-failed': {
    intent: 'error',
    label: 'Associate Failed',
    hint: 'AWS could not associate this resource discovery, so the accounts behind it report nothing.',
  },
  'disassociate-failed': {
    intent: 'error',
    label: 'Disassociate Failed',
    hint: 'AWS could not disassociate this resource discovery. It still exists and still counts against the association quota.',
  },

  'delete-complete': {
    intent: 'neutral',
    label: 'Deleted',
    hint: 'AWS reports this is gone. AWS keeps returning deleted rows as tombstones for several minutes afterwards, which is why Prism judges by state and never by counting rows.',
  },
  'disassociate-complete': {
    intent: 'neutral',
    label: 'Disassociated',
    hint: 'AWS reports this resource discovery association is gone.',
  },

  // ── THE THREE THAT MEAN "THE OWNING ACCOUNT LEFT THE ORGANIZATION" ─────────
  //
  // Drawn as error and warning rather than as progress, because that is what
  // they are. There is no work Prism can do from outside a departed account: no
  // grant it can attach, no session it can open, no retry that changes the
  // answer. Rendering these in the same grey-blue as "Creating" is the single
  // most expensive mislabel in this vocabulary — it turns "this will never
  // succeed and may still be billing" into "nearly done".
  'isolate-in-progress': {
    intent: 'error',
    label: 'Account Leaving Org',
    hint: `${IPAM_ISOLATED_SENTENCE} AWS is isolating this resource. Prism cannot reach the account to act on it, and retrying will not change that. Anything billable inside that account keeps billing to that account.`,
  },
  'isolate-complete': {
    intent: 'error',
    label: 'Account Left Org',
    hint: `${IPAM_ISOLATED_SENTENCE} Prism has no way in from outside it — there is no grant to attach and no session to open, so retrying is pointless. The resource may still exist and still bill inside that account. Getting the account back into the organization is the only path.`,
  },
  'restore-in-progress': {
    intent: 'warning',
    label: 'Account Rejoining',
    hint: 'The AWS account that owns this resource is being restored to the organization. This is the recovery from an isolated state and it IS worth waiting for — unlike the other two isolate states, this one settles. Nothing else here will work until it does.',
  },

  // --- IpamPoolCidrState: a SIXTH enum, outside the five above. It shares no
  //     values with them, which is why both live in one table without colliding.
  //     `failed-provision` and `failed-deprovision` are real terminal states and
  //     IpamPoolCidr.FailureReason is the only thing that says why — the pool
  //     detail page renders that reason on the row rather than behind a hover.

  'pending-provision': {
    intent: 'info',
    label: 'Provisioning',
    hint: 'AWS accepted the CIDR and is provisioning it into the pool; this usually settles in about 20 seconds.',
  },
  provisioned: {
    intent: 'success',
    label: 'Provisioned',
    hint: 'The CIDR is in the pool and can be allocated from.',
  },
  'failed-provision': {
    intent: 'error',
    label: 'Provision Failed',
    hint: 'AWS refused the CIDR. The failure reason on this row is the whole diagnosis — the CIDR was NOT added to the pool.',
  },
  'pending-deprovision': {
    intent: 'info',
    label: 'Deprovisioning',
    hint: 'AWS is removing the CIDR from the pool. It STILL HOLDS THE CIDR and still counts against the pool’s CIDR quota until this settles.',
  },
  deprovisioned: {
    intent: 'neutral',
    label: 'Deprovisioned',
    hint: 'The CIDR is out of the pool and no longer counts against its quota.',
  },
  'failed-deprovision': {
    intent: 'error',
    label: 'Deprovision Failed',
    hint: 'AWS could not remove the CIDR. IT IS STILL PROVISIONED and still counts against the pool’s quota — usually because the pool still holds allocations. This one is retryable.',
  },

  // --- RAM ResourceShareStatus, for the pool's shares table. Upper-case on the
  //     wire; the resolver lower-cases, so they are keyed lower-case here.

  pending: {
    intent: 'warning',
    label: 'Pending',
    hint: 'The share was created but the receiving account has not accepted the invitation, so the pool is NOT usable there yet.',
  },
  active: {
    intent: 'success',
    label: 'Active',
    hint: 'AWS reports the share is active and the pool is available to the principals it names.',
  },
  failed: {
    intent: 'error',
    label: 'Failed',
    hint: 'AWS reports this share failed. The pool is not shared with anyone through it.',
  },
  deleting: {
    intent: 'info',
    label: 'Deleting',
    hint: 'AWS is still deleting this share.',
  },
  deleted: {
    intent: 'neutral',
    label: 'Deleted',
    hint: 'AWS reports this share is gone.',
  },
};

// The isolate family, exported so a caller can ask "is this an org departure"
// without re-spelling the three strings. `restore-in-progress` is deliberately
// NOT in it: it is the recovery, and a page that grouped it with the other two
// would tell an operator to give up on the one case that resolves itself.
export const IPAM_ISOLATED_STATES = ['isolate-in-progress', 'isolate-complete'];

const normalize = (state) => String(state ?? '').trim().toLowerCase();

// ipamStateMeta resolves one raw AWS state string.
//
// Returns null for an ABSENT value — empty string, null, undefined — because
// absent is not a state and must not be drawn as one. An unknown state is a
// third case again: it comes back with the mechanical label and the neutral
// intent, plus `known: false` so a caller can say so out loud.
export function ipamStateMeta(state) {
  const key = normalize(state);
  if (key === '') return null;
  const hit = IPAM_STATE_META[key];
  if (hit) return { ...hit, known: true, raw: String(state) };
  // Amber, never grey. See the header note: grey is what the DECIDED, benign
  // states wear, so an unknown value drawn grey reads as one of them.
  return {
    intent: 'warning',
    label: formatStatusLabel(state),
    hint: `Prism has no rule for the AWS state "${state}", so it cannot say whether this is healthy, still working or stuck. The value is shown exactly as AWS reported it. Treat it as unverified rather than as either good or bad.`,
    known: false,
    raw: String(state),
  };
}

export const ipamStateIntent = (state) => ipamStateMeta(state)?.intent || 'neutral';
export const ipamStateLabel = (state) => ipamStateMeta(state)?.label || '';

// True when the state means the owning account has left the organization, so a
// caller can suppress a retry affordance that cannot possibly work.
export const ipamStateIsIsolated = (state) => IPAM_ISOLATED_STATES.includes(normalize(state));

// IpamStateChip — the chip both pool screens draw, so a state cannot be coloured
// one way in the tree and another way on the detail page.
//
// AN ABSENT STATE IS A MUTED EM-DASH WITH A TOOLTIP, NEVER A CHIP. A chip is a
// statement that AWS told Prism something; drawing one for a value Prism does
// not have would turn "we never read this" into a clean-looking answer, which is
// the exact false reassurance this capability is written to avoid.
export function IpamStateChip({ state, freshness = true, sx }) {
  const meta = ipamStateMeta(state);
  if (!meta) {
    return (
      <Tooltip title="Prism has not recorded a state for this yet.">
        <span style={{ color: CK.muted, fontFamily: FONT_BODY }}>&mdash;</span>
      </Tooltip>
    );
  }
  const title = freshness ? `${meta.hint} ${IPAM_STATE_FRESHNESS}` : meta.hint;
  return (
    <Tooltip title={title}>
      <Chip size="small" label={meta.label} sx={{ ...statusChipSx(meta.intent), ...sx }} />
    </Tooltip>
  );
}

export default IPAM_STATE_META;

// The utilization thresholds THIS CAPABILITY judges by, in one place.
//
// portalStyles exports UTILIZATION_THRESHOLDS = {amber: 70, red: 90} as the
// portal-wide default. IPAM deliberately runs looser — an address plan sitting at
// 75% is normal, not a warning — but "deliberately" only holds if every screen
// agrees. It did not: Address Space passed {80, 95} and the Dashboard passed
// nothing and got {70, 90}, so the same VPC at 75% was amber on one page and
// green on the other, and the Dashboard row is CLICKABLE straight through to
// Address Space — the colour changed under the operator's own click.
//
// Pass this to every <UtilizationBar> in the capability. A bar with no thresholds
// prop is a bar silently judging by someone else's rules.
export const IPAM_UTILIZATION_THRESHOLDS = { amber: 80, red: 95 };
